import {Box, Button, Stack, Typography, capitalize} from '@mui/material';
import React, {Fragment} from 'react';

import {
	BookingScheduledEnum,
	OdontoBookingTaggingEnum,
	WellBornTaggingEnum,
} from 'main/analytics/gaTagsEnum';
import {
	AlertInfo,
	AlertModal,
	CTAButtonFixed,
	CopySection,
	MainFooter,
	StyledGrid,
} from 'presentation/components';
import {CallPhoneButton} from 'presentation/components/CallPhoneButton';
import {Loading} from 'presentation/components/Loading/Loading';
import {ModalError} from 'presentation/components/ModalError';
import {PhonesDrawer} from 'presentation/components/PhonesDrawer';
import {PhonesMenu} from 'presentation/components/PhonesMenu';
import {ChatRoomsEnum, CopySectionMaskEnum, OperationStateTypesEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {
	AppointmentInstructions,
	ConfirmationCard,
} from 'presentation/pages/private/booking/components';
import {TELEMEDICINE_INFO} from 'presentation/pages/private/booking/constants';

import {translations} from 'presentation/translations-new';

import {WhoIndicatesTagParametricEnum} from 'domain/usecases/private/home/who-indicates';
import {WhoIndicatesCard} from 'presentation/components/WhoIndicatesCard';
import {SquareLabelTime} from 'presentation/modules/booking/shared/ui/components/SquareLabelTime';
import {CancelBookingButton} from 'presentation/modules/myAppointments/ui/components/CancelBookingButton';
import {ConcludedAlert} from 'presentation/modules/myAppointments/ui/components/ConcludedAlert';
import {ConfirmToGoButton} from 'presentation/modules/myAppointments/ui/components/ConfirmToGoButton';
import {PrintTemplate} from 'presentation/modules/myAppointments/ui/components/PrintTemplate';
import {SaveToCalendar} from 'presentation/pages/private/home/components';
import {StyledBox} from './styles';
import {MyAppointmentDetailsContentProps} from './types';

import {ProtocolCard} from 'presentation/modules/booking/shared/ui/components/ProtocolCard';
import {CityCard} from '../CityCard';
import {useMyAppointmentDetailsContent} from './hooks/useMyAppointmentDetailsContent';
import {Attachments} from 'presentation/modules/myAppointments/ui/components/Attachments';
import {useGetAppointmentAttachmentsQuery} from '../../queries/useGetAppointmentAttachmentsQuery';
import {FavoriteDoctorsLikeButton} from 'presentation/modules/booking/favoriteDoctors/components/FavoriteDoctorsLikeButton';

const {
	attendanceUnit,
	attendance,
	professionalAndDate,
	phones,
	download,
	notice,
	regularizeNow,
	close,
	serverError,
	tryAgain,
	serverErrorTitle,
	callToChat,
	call,
	referAndEarn,
} = translations['pt-br'].commons;
const {openMap} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;
const {appointmentCard, rescheduleExamBlockedModal, uploadAttachments} =
	translations['pt-br'].privateFlow.myAppointments;
const {contractSuspendedModalTitle, contractSuspendedModalDescription} =
	translations['pt-br'].others.contractSuspendedErrorModal;
const {choosePhoneNumber} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;
const {beBornWellInfos} = translations['pt-br'].privateFlow.bookingFlow.beBornWellFlow;
const {appointmentSummary} = translations['pt-br'].privateFlow.bookingFlow;

export function MyAppointmentDetailsContent({
	flowType,
	bookedAppointment,
}: MyAppointmentDetailsContentProps) {
	const {
		colors,
		isContractSuspendedModalActiveState,
		setIsContractSuspendedModalActiveState,
		isExamBlockedModalActiveState,
		setIsExamBlockedModalActiveState,
		isBornWellActiveState,
		setIsBornWellActiveState,
		openPhoneDrawerState,
		anchorElState,
		isExamsTypesLocationError,
		isMobile,
		isTablet,
		betweenMobileAndDesktop,
		isLaptop,
		largerDevices,
		isDesktop,
		isPrinting,
		printContent,
		printRef,
		callChatRoom,
		callChatRoomIsLoading,
		isTelehealth,
		isSurgery,
		iconName,
		showEnterRoom,
		rebookingBtnText,
		clinicName,
		formattedAddress,
		phoneNumbers,
		providerName,
		isAdvanceDiagnosticsExam,
		cdPrestador,
		specialtyName,
		specialtyCode,
		subSpecialtyCode,
		protocolNumber,
		isConfirmed,
		day,
		monthName,
		formatted,
		handleOpenGoogleMaps,
		handleRebooking,
		isRebookingLoading,
		listPhoneNumbers,
		listPhoneNumbersAlert,
		isPhoneNumberMenuOpen,
		handleOpenPhoneMenu,
		handleTogglePhoneDrawer,
		handleClosePhoneMenu,
		calendarEvent,
		hideProtocolCard,
		handleOpenTelehealthRoom,
		setOperationState,
		isUploadFileTeleFeatureFlagEnabled,
	} = useMyAppointmentDetailsContent({flowType, bookedAppointment});

	const {
		data: attachmentsData,
		isLoading: isLoadingAttachments,
		refetch: refetchAttachments,
	} = useGetAppointmentAttachmentsQuery(
		isUploadFileTeleFeatureFlagEnabled && isTelehealth ? bookedAppointment.bookingNumber : '',
	);

	const files = Array.isArray(attachmentsData) ? attachmentsData : [];

	const confirmToGoOrEnterRoomBtn = showEnterRoom ? (
		<Button
			size="large"
			onClick={handleOpenTelehealthRoom}
			endIcon={<Icon name="ben-export" />}>
			{appointmentCard.teleHealth.goToTelehealth}
		</Button>
	) : (
		<ConfirmToGoButton variant="button" appointment={bookedAppointment} />
	);

	return (
		<Fragment>
			{isConfirmed ? (
				<Fragment data-tag-id={BookingScheduledEnum.CONFIRMOU_PRESENCA_COM_SUCESSO}>
					<ConcludedAlert text={appointmentCard.confirmed} />
				</Fragment>
			) : null}
			<StyledGrid.Root p={isMobile ? 2 : 4}>
				<StyledGrid.Item sm={12} md={12} lg={8}>
					<Stack gap={2}>
						<Typography variant="subtitle2" color={colors.light.text.secondary}>
							{appointmentSummary}
						</Typography>
						<ConfirmationCard.Root sx={{position: 'relative'}}>
							<ConfirmationCard.Header subheader={professionalAndDate}>
								<Stack flexDirection={'row'} gap={2} alignItems={'start'}>
									<SquareLabelTime day={day} month={monthName} />
									<Stack width={'100%'} gap={'2px'}>
										<Typography variant="subtitle2" fontSize={'1.125rem'}>
											{`${capitalize(formatted)}`}
										</Typography>
										{providerName && !isAdvanceDiagnosticsExam ? (
											<ConfirmationCard.Content
												iconName="medical-services-filled"
												primaryText={providerName}
												disableMargin
											/>
										) : null}

										{specialtyName ? (
											<ConfirmationCard.Content
												iconName={iconName}
												primaryText={specialtyName}
												disableMargin
											/>
										) : null}
									</Stack>
								</Stack>
								<Stack
									sx={{
										width: '100%',
										mt: isMobile ? 1 : 0,
										flexDirection: isMobile ? 'column' : 'row',
										alignItems: 'center',
										justifyContent: 'end',
										gap: '8px',
									}}
									className="remove-me">
									<Button
										disabled={isPrinting}
										variant="outlined"
										endIcon={
											isPrinting ? (
												<Loading size={24} color="inherit" />
											) : (
												<Icon name="download" size={24} />
											)
										}
										fullWidth={isMobile}
										onClick={printContent}>
										{download}
									</Button>
									<SaveToCalendar calendarEvent={calendarEvent} />
								</Stack>
							</ConfirmationCard.Header>
							{specialtyCode && cdPrestador ? (
								<FavoriteDoctorsLikeButton
									cdEspecialidade={Number(specialtyCode)}
									cdSubEspecialidade={Number(subSpecialtyCode)}
									cdPrestador={cdPrestador}
									selected={bookedAppointment.isFavorite}
									sx={{position: 'absolute', top: 16, right: 16}}
								/>
							) : null}
						</ConfirmationCard.Root>
						<Box
							display="grid"
							gridTemplateColumns={isTablet ? '1fr' : '1fr 1fr'}
							gap={2}>
							<ConfirmationCard.Root>
								<ConfirmationCard.Header
									subheader={isTelehealth ? attendance : attendanceUnit}
								/>
								<Stack
									flexDirection={'column'}
									alignItems={'start'}
									justifyContent="space-between"
									gap={1}>
									<ConfirmationCard.Content
										iconName={
											isTelehealth
												? 'remember-me-filled'
												: 'location-on-filled'
										}
										primaryText={
											isTelehealth ? TELEMEDICINE_INFO.name : clinicName
										}
										secondaryText={isTelehealth ? '' : formattedAddress}
										secondaryTextColor="text.secondary"
									/>
									{!isTelehealth ? (
										<Stack
											sx={{
												width: '100%',
												mt: isMobile ? 1 : 0,
												flexDirection: isMobile ? 'column' : 'row',
												alignItems: 'center',
												justifyContent: 'end',
												gap: '8px',
											}}
											className="remove-me">
											<Button
												sx={{minWidth: 'fit-content'}}
												onClick={handleOpenGoogleMaps}
												fullWidth={isTablet}
												variant="outlined"
												endIcon={<Icon name="map" />}>
												{openMap}
											</Button>
										</Stack>
									) : null}
								</Stack>
							</ConfirmationCard.Root>
							{isTelehealth ? (
								<ProtocolCard protocolNumber={protocolNumber} />
							) : (
								<CityCard flowType={flowType} formattedAddress={formattedAddress} />
							)}
						</Box>

						{hideProtocolCard ? null : <ProtocolCard protocolNumber={protocolNumber} />}

						{phoneNumbers && phoneNumbers?.length > 0 ? (
							<ConfirmationCard.Root>
								<ConfirmationCard.Header subheader={phones} />
								<Stack direction={isTablet ? 'column' : 'row'} spacing={1}>
									{phoneNumbers.map((phoneNumber, index) => (
										<CopySection
											key={`${index}-${phoneNumber}`}
											value={phoneNumber}
										/>
									))}
								</Stack>
							</ConfirmationCard.Root>
						) : null}

						{isUploadFileTeleFeatureFlagEnabled && isTelehealth ? (
							<>
								<Typography variant="subtitle2" color={colors.light.text.secondary}>
									{uploadAttachments}
								</Typography>
								<Attachments
									files={files}
									loading={isLoadingAttachments}
									attendanceId={bookedAppointment.bookingNumber}
									showUpload={true}
									onUploadSuccess={refetchAttachments}
									acceptAttachment={bookedAppointment.acceptsAttachment}
								/>
							</>
						) : null}

						{isMobile ? (
							<CTAButtonFixed.Root>
								<Stack flexDirection="row" gap={1}>
									<CancelBookingButton appointment={bookedAppointment} />
									{isSurgery ? null : (
										<CTAButtonFixed.Action
											fullWidth
											size="large"
											variant="outlined"
											onClick={handleRebooking}
											data-tag-id={
												BookingScheduledEnum.CLICK_REMARCAR_CONSULTA
											}
											endIcon={
												isRebookingLoading ? (
													<Loading color="inherit" size={16} />
												) : null
											}
											disabled={isRebookingLoading}>
											{rebookingBtnText}
										</CTAButtonFixed.Action>
									)}
								</Stack>
								{confirmToGoOrEnterRoomBtn}
							</CTAButtonFixed.Root>
						) : null}

						{isLaptop ? null : (
							<Stack
								alignItems={largerDevices ? 'center' : 'initial'}
								flexDirection={largerDevices ? 'row' : 'column'}
								justifyContent={'end'}
								gap={largerDevices ? 1 : 0}>
								<Stack
									py={1}
									flex={2}
									flexDirection="row"
									gap={1}
									justifyContent={'end'}>
									<CancelBookingButton appointment={bookedAppointment} />

									{isSurgery ? null : (
										<Button
											size="large"
											variant="outlined"
											onClick={handleRebooking}
											data-tag-id={
												BookingScheduledEnum.CLICK_REMARCAR_CONSULTA
											}
											disabled={isRebookingLoading}
											endIcon={
												isRebookingLoading ? (
													<Loading color="inherit" size={16} />
												) : null
											}>
											{rebookingBtnText}
										</Button>
									)}

									{confirmToGoOrEnterRoomBtn}
								</Stack>
							</Stack>
						)}
					</Stack>
				</StyledGrid.Item>

				<StyledGrid.Item sm={12} md={12} lg={4}>
					<AppointmentInstructions appointment={bookedAppointment} />

					<Stack gap={2} mt={2}>
						<Typography
							fontWeight={600}
							variant="body2"
							color={colors.light.text.secondary}>
							{referAndEarn}
						</Typography>
						<WhoIndicatesCard
							dataTagId={OdontoBookingTaggingEnum.CLICK_SAIBA_MAIS_QUEM_INDICA}
							$showAsBanner={!isMobile && !isDesktop}
							pathParametric={WhoIndicatesTagParametricEnum.BOOKING}
						/>
					</Stack>

					{betweenMobileAndDesktop ? (
						<Box mt={2}>
							<Stack>
								<Stack py={1} flexDirection="row" gap={1} justifyContent={'end'}>
									<CancelBookingButton appointment={bookedAppointment} />
									{isSurgery ? null : (
										<Button
											size="large"
											variant="outlined"
											onClick={handleRebooking}
											data-tag-id={
												BookingScheduledEnum.CLICK_REMARCAR_CONSULTA
											}
											endIcon={
												isRebookingLoading ? (
													<Loading color="inherit" size={16} />
												) : null
											}
											disabled={isRebookingLoading}>
											{rebookingBtnText}
										</Button>
									)}

									{confirmToGoOrEnterRoomBtn}
								</Stack>
							</Stack>
						</Box>
					) : null}
				</StyledGrid.Item>
			</StyledGrid.Root>
			<StyledBox>
				<MainFooter />
			</StyledBox>
			<PrintTemplate
				bookedAppointment={bookedAppointment}
				flowType={flowType}
				printRef={printRef}
				files={files}
			/>

			<ModalError
				isFullScreen={false}
				maxWidth={420}
				onClose={() => setIsContractSuspendedModalActiveState(false)}
				headerTitle={notice}
				title={contractSuspendedModalTitle}
				description={contractSuspendedModalDescription.line1}
				subDescription={contractSuspendedModalDescription.line2}
				open={isContractSuspendedModalActiveState}
				firstButton={{
					title: regularizeNow,
					handleClick: async () => {
						await callChatRoom(ChatRoomsEnum.DEBIT_NEGOTIATION);
					},
					endIcon: callChatRoomIsLoading ? <Loading size={16} color="inherit" /> : null,
				}}
				disabled={callChatRoomIsLoading}
				secondButton={{
					title: close,
					handleClick: () => setIsContractSuspendedModalActiveState(false),
				}}
			/>

			<AlertModal
				open={isExamBlockedModalActiveState || isBornWellActiveState}
				onClose={() => {
					setIsExamBlockedModalActiveState(false);
					setIsBornWellActiveState(false);
				}}
				variant="info"
				title={rescheduleExamBlockedModal.title}
				headerTitle={notice}
				description={rescheduleExamBlockedModal.description}
				fullScreen={isMobile}
				fullWidth={isMobile}
				slotProps={{paper: isMobile ? {} : {style: {maxWidth: 420}}}}
				footerContent={
					<Stack gap={1} width={'100%'}>
						<Button
							size="large"
							fullWidth
							data-tag-id={
								isBornWellActiveState
									? WellBornTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_NASCER_BEM_REMARCACAO
									: undefined
							}
							onClick={() => {
								if (isBornWellActiveState) {
									callChatRoom(ChatRoomsEnum.BE_BORN_WELL);
									return;
								}
								callChatRoom(ChatRoomsEnum.APP);
							}}>
							{callToChat}
						</Button>
						{isBornWellActiveState ? (
							<Button
								variant={'outlined'}
								size="large"
								data-tag-id={
									WellBornTaggingEnum.CLICK_EXIBIR_TELEFONES_NASCER_BEM_REMARCACAO
								}
								endIcon={<Icon name="phone-filled" />}
								onClick={handleOpenPhoneMenu}>
								{call}
							</Button>
						) : (
							<CallPhoneButton
								phoneNumbers={listPhoneNumbers}
								phoneNumbersAlert={listPhoneNumbersAlert}
							/>
						)}
					</Stack>
				}
			/>

			<AlertModal
				open={isExamsTypesLocationError}
				onClose={() => setOperationState(OperationStateTypesEnum.NONE)}
				description={serverError}
				title={serverErrorTitle}
				headerTitle={notice}
				variant="info"
				footerContent={
					<Button variant="contained" size="large" fullWidth onClick={handleRebooking}>
						{tryAgain}
					</Button>
				}
			/>
			{isMobile ? (
				<PhonesDrawer.Root
					open={openPhoneDrawerState}
					toggleDrawer={handleTogglePhoneDrawer}
					title={choosePhoneNumber}>
					<AlertInfo.Root>{[beBornWellInfos.alert]}</AlertInfo.Root>
					<PhonesDrawer.Simple
						mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
						phoneNumbers={[beBornWellInfos.telephoneNumber]}
					/>
				</PhonesDrawer.Root>
			) : (
				<PhonesMenu.Root
					open={isPhoneNumberMenuOpen}
					onClose={handleClosePhoneMenu}
					anchorEl={anchorElState}
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}
					transformOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}>
					<Box pb={2}>
						<AlertInfo.Root>{[beBornWellInfos.alert]}</AlertInfo.Root>
					</Box>
					<PhonesDrawer.Simple
						mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
						phoneNumbers={[beBornWellInfos.telephoneNumber]}
					/>
				</PhonesMenu.Root>
			)}
		</Fragment>
	);
}
